import { baseApi } from '@/lib/store/api/baseQuery'

export type VerifiedAddress = {
    line_1: string
    line_2: string
    city: string
    state: string
    zip: string
}

export type VerifiedSex = 'Male' | 'Female' | ''

export const MAX_VERIFIED_ADDRESSES = 7

/** In-memory signup profile populated from the Verified 1-click lookup. Never persisted to storage. */
export interface VerifiedProfile {
    firstName: string
    lastName: string
    birthDate: string
    gender: VerifiedSex
    addresses: VerifiedAddress[]
    selectedAddressIndex: number
}

export interface VerifyIdentityRequest {
    birth_date: string
    ssn?: string
}

interface VerifyIdentityRawAddress {
    line_1?: string
    line_2?: string
    city?: string
    state?: string
    zip?: string
}

export interface VerifyIdentityRawResponse {
    error_code?: string
    first_name?: string
    last_name?: string
    email?: string
    date_of_birth?: string
    sex?: string
    address?: VerifyIdentityRawAddress
    addresses?: VerifyIdentityRawAddress[]
}

const addressIdentity = (address: VerifiedAddress): string =>
    [address.line_1, address.line_2, address.city, address.state, address.zip].join('|').toLowerCase()

const normalizeAddress = (raw: VerifyIdentityRawAddress): VerifiedAddress | null => {
    const address: VerifiedAddress = {
        line_1: String(raw.line_1 ?? '').trim(),
        line_2: String(raw.line_2 ?? '').trim(),
        city: String(raw.city ?? '').trim(),
        state: String(raw.state ?? '').trim(),
        // Verified returns ZIP+4 for some records; the patients API stores five digits.
        zip: String(raw.zip ?? '')
            .trim()
            .split('-')[0],
    }
    return Object.values(address).some(Boolean) ? address : null
}

const selectOrAddAddress = (draft: VerifiedProfile, address: VerifiedAddress): boolean => {
    const normalizedAddress = normalizeAddress(address)
    if (!normalizedAddress) return false
    const existingIndex = draft.addresses.findIndex(
        (candidate) => addressIdentity(candidate) === addressIdentity(normalizedAddress),
    )
    if (existingIndex !== -1) {
        draft.selectedAddressIndex = existingIndex
        return true
    }
    if (draft.addresses.length >= MAX_VERIFIED_ADDRESSES) return false
    draft.selectedAddressIndex = draft.addresses.push(normalizedAddress) - 1
    return true
}

const replaceAddress = (
    draft: VerifiedProfile,
    original: VerifiedAddress,
    updated: VerifiedAddress,
): boolean => {
    const normalizedOriginal = normalizeAddress(original)
    const normalizedUpdated = normalizeAddress(updated)
    if (!normalizedOriginal || !normalizedUpdated) return false

    const originalIndex = draft.addresses.findIndex(
        (candidate) => addressIdentity(candidate) === addressIdentity(normalizedOriginal),
    )
    if (originalIndex === -1) return selectOrAddAddress(draft, normalizedUpdated)

    const duplicateIndex = draft.addresses.findIndex(
        (candidate) => addressIdentity(candidate) === addressIdentity(normalizedUpdated),
    )
    if (duplicateIndex !== -1 && duplicateIndex !== originalIndex) {
        draft.addresses.splice(originalIndex, 1)
        draft.selectedAddressIndex = duplicateIndex > originalIndex ? duplicateIndex - 1 : duplicateIndex
        return true
    }

    draft.addresses[originalIndex] = normalizedUpdated
    draft.selectedAddressIndex = originalIndex
    return true
}

const normalizeSex = (sex: unknown): VerifiedSex => {
    const value = String(sex ?? '')
        .trim()
        .toLowerCase()
    if (value === 'm' || value === 'male') return 'Male'
    if (value === 'f' || value === 'female') return 'Female'
    return ''
}

const uniqueAddresses = (rawAddresses: VerifyIdentityRawAddress[]): VerifiedAddress[] => {
    const byIdentity = new Map<string, VerifiedAddress>()
    rawAddresses.forEach((rawAddress) => {
        const address = normalizeAddress(rawAddress)
        if (!address) return
        const identity = addressIdentity(address)
        if (!byIdentity.has(identity)) byIdentity.set(identity, address)
    })
    return Array.from(byIdentity.values()).slice(0, MAX_VERIFIED_ADDRESSES)
}

/** Returns null when Verified reports a lookup failure. */
export const transformVerifyIdentityResponse = (raw: VerifyIdentityRawResponse): VerifiedProfile | null => {
    if (raw?.error_code) return null

    return {
        firstName: raw?.first_name ?? '',
        lastName: raw?.last_name ?? '',
        birthDate: raw?.date_of_birth ?? '',
        gender: normalizeSex(raw?.sex),
        addresses: uniqueAddresses(raw?.addresses ?? (raw?.address ? [raw.address] : [])),
        selectedAddressIndex: 0,
    }
}

export interface CreatePatientRequest {
    first_name: string
    last_name: string
    email: string
    date_of_birth: string
    sex_assigned_at_birth: string
    phone_number: string
    ssn?: string
    home_address: VerifiedAddress
    consents: {
        automated_texts: boolean
        hipaa_privacy_practices: boolean
        privacy_policy: boolean
        telehealth_informed_consent: boolean
        terms_of_use: boolean
        texting: boolean
    }
    campaign_attributions: Record<string, string>
    signup_id: string
    signup_sku_id: string
    signup_channel: string
    access_code: string
}

export type PatientRegistrationStatus = 'pending' | 'verified' | 'failed'

/** Mercy poll payload from GET /api/external/mercy/registration_status. */
export interface PatientRegistrationStatusResponse {
    registration_status: PatientRegistrationStatus
    mymercy_account_exists?: boolean
}

/** Body for POST /api/external/mercy/registration/retry (about-you / review-info fields). */
export interface MercyRegistrationRetryRequest {
    first_name: string
    last_name: string
    date_of_birth: string
    email: string
    sex_assigned_at_birth: string
    home_address: {
        line_1: string
        line_2: string
        city: string
        state: string
        zip: string
    }
}

type CacheMutationResult = { data: null } | { error: { status: 'CUSTOM_ERROR'; error: string } }

const patchVerifiedProfile = (
    dispatch: (action: unknown) => unknown,
    recipe: (draft: VerifiedProfile) => string | undefined,
): CacheMutationResult => {
    let error: string | undefined = 'No verified signup profile in cache'
    dispatch(
        signupApi.util.updateQueryData('getVerifiedSignupProfile', undefined, (draft) => {
            if (draft) error = recipe(draft)
        }),
    )
    return error ? { error: { status: 'CUSTOM_ERROR', error } } : { data: null }
}

export const signupApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getVerifiedSignupProfile: builder.query<VerifiedProfile | null, void>({
            queryFn: () => ({ data: null }),
            keepUnusedDataFor: Infinity,
        }),

        selectVerifiedAddress: builder.mutation<null, VerifiedAddress>({
            queryFn: (address, { dispatch }) =>
                patchVerifiedProfile(dispatch, (draft) =>
                    selectOrAddAddress(draft, address) ? undefined : 'Address limit reached',
                ),
        }),

        updateVerifiedAddress: builder.mutation<null, { original: VerifiedAddress; updated: VerifiedAddress }>({
            queryFn: ({ original, updated }, { dispatch }) =>
                patchVerifiedProfile(dispatch, (draft) =>
                    replaceAddress(draft, original, updated) ? undefined : 'No verified signup profile in cache',
                ),
        }),

        resetVerifiedSignupProfile: builder.mutation<null, void>({
            queryFn: (_arg, { dispatch }) => {
                dispatch(signupApi.util.upsertQueryData('getVerifiedSignupProfile', undefined, null))
                return { data: null }
            },
        }),

        verifyIdentity: builder.mutation<VerifiedProfile | null, VerifyIdentityRequest>({
            query: (body) => ({ url: '/api/external/verified', method: 'POST', body }),
            transformResponse: transformVerifyIdentityResponse,
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled.catch(() => ({ data: null }))
                dispatch(signupApi.util.upsertQueryData('getVerifiedSignupProfile', undefined, data))
            },
        }),

        createPatient: builder.mutation<void, CreatePatientRequest>({
            query: (body) => ({ url: '/api/external/patients', method: 'POST', body }),
            invalidatesTags: ['User', 'PatientRegistrationStatus'],
        }),

        getRegistrationStatus: builder.query<PatientRegistrationStatusResponse, void>({
            query: () => '/api/external/mercy/registration_status',
            providesTags: ['PatientRegistrationStatus'],
            async onQueryStarted(_arg, { queryFulfilled }) {
                const {
                    handleMercyVerificationStatusFailed,
                    handleMercyVerificationStatusTimeout,
                    handleMercyVerificationStatusVerified,
                    isMercyVerificationWaitAwaitingFinalPoll,
                    isMercyVerificationWaitSessionPolling,
                } = await import('@/tenants/mercy/lib/mercyVerificationWaitSession')

                // No-op unless Mercy signup has an active wait/poll session.
                if (!isMercyVerificationWaitSessionPolling()) {
                    await queryFulfilled.catch(() => undefined)
                    return
                }

                try {
                    const { data } = await queryFulfilled
                    if (!isMercyVerificationWaitSessionPolling()) return

                    if (data.registration_status === 'verified') {
                        handleMercyVerificationStatusVerified({
                            mymercyAccountExists: data.mymercy_account_exists === true,
                        })
                    } else if (data.registration_status === 'failed') {
                        handleMercyVerificationStatusFailed()
                    } else if (isMercyVerificationWaitAwaitingFinalPoll()) {
                        // Wall-clock expired; this response is the last check before unlock.
                        handleMercyVerificationStatusTimeout()
                    }
                } catch {
                    if (isMercyVerificationWaitAwaitingFinalPoll()) {
                        handleMercyVerificationStatusTimeout()
                        return
                    }
                    // Transient poll failures (network blips, brief 5xx) must not unlock
                    // the form; keep polling until verified, failed, or client timeout.
                }
            },
        }),

        createMyMercyAccount: builder.mutation<void, { username: string; password: string }>({
            query: (body) => ({
                url: '/api/external/mercy/mymercy/account',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PatientRegistrationStatus'],
        }),

        /** Mercy-only: retry Persona and/or Epic resolve for an existing GM patient. */
        retryMercyRegistration: builder.mutation<void, MercyRegistrationRetryRequest>({
            query: (body) => ({
                url: '/api/external/mercy/registration/retry',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['User', 'PatientRegistrationStatus'],
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetVerifiedSignupProfileQuery,
    useSelectVerifiedAddressMutation,
    useUpdateVerifiedAddressMutation,
    useResetVerifiedSignupProfileMutation,
    useVerifyIdentityMutation,
    useCreatePatientMutation,
    useGetRegistrationStatusQuery,
    useLazyGetRegistrationStatusQuery,
    useCreateMyMercyAccountMutation,
    useRetryMercyRegistrationMutation,
} = signupApi
